/**
 * Small, pure presentation helpers. They are deliberately independent of the
 * WebSocket and the 3-D scene so the HUD can be tested without a browser.
 */

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/** Whole watts, never negative. */
export function formatPower(watts: number): string {
  if (!Number.isFinite(watts)) return "0";
  return String(Math.round(Math.max(0, watts)));
}

/** Metres per second to km/h with one decimal, e.g. "32.4". */
export function formatSpeed(mps: number): string {
  if (!Number.isFinite(mps)) return "0.0";
  return (Math.max(0, mps) * 3.6).toFixed(1);
}

/** Grade in percent with an explicit sign, e.g. "+3.5%" or "-1.2%". */
export function formatGrade(percent: number): string {
  if (!Number.isFinite(percent)) return "0.0%";
  const value = Math.abs(percent) < 0.05 ? 0 : percent;
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

/** Metres to kilometres with two decimals, e.g. "12.34". */
export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters)) return "0.00";
  return (Math.max(0, meters) / 1000).toFixed(2);
}

/** Draft fraction (0..1) as a whole percentage, e.g. "24%". */
export function formatDraft(draft: number): string {
  if (!Number.isFinite(draft)) return "0%";
  return `${Math.round(clamp(draft, 0, 1) * 100)}%`;
}

/** Difficulty fraction (0..1) as a whole percentage, e.g. "50%". */
export function difficultyLabel(difficulty: number): string {
  return `${Math.round(clamp(difficulty, 0, 1) * 100)}%`;
}

/** Elapsed milliseconds as m:ss (or h:mm:ss past an hour). */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "0:00";
  const totalSeconds = Math.floor(ms / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function formatLap(lap: number): string {
  if (!Number.isFinite(lap) || lap < 1) return "1";
  return String(Math.floor(lap));
}
